// ============================================
// パネル演出（グリッド・データストリーム・区切り線・パルスドット）
// ============================================

import type p5 from 'p5';
import { HUD_COLORS } from '../../config/hud-colors';

/**
 * drawPanelGrid() - パネル背景にグリッドパターンを描画
 * 20px間隔の薄いシアンのグリッド線
 */
export function drawPanelGrid(
    p: p5,
    x: number,
    y: number,
    width: number,
    height: number
): void {
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    const gridSize = 20;

    ctx.save();
    ctx.strokeStyle = `rgba(${HUD_COLORS.hudLine[0]}, ${HUD_COLORS.hudLine[1]}, ${HUD_COLORS.hudLine[2]}, 0.03)`;
    ctx.lineWidth = 1;

    // 垂直線
    for (let gx = x; gx <= x + width; gx += gridSize) {
        ctx.beginPath();
        ctx.moveTo(gx, y);
        ctx.lineTo(gx, y + height);
        ctx.stroke();
    }

    // 水平線
    for (let gy = y; gy <= y + height; gy += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, gy);
        ctx.lineTo(x + width, gy);
        ctx.stroke();
    }

    ctx.restore();
}

/**
 * drawDataStream() - パネル端に流れる光を描画
 * 2px幅の縦方向グラデーション、frameCountでオフセットアニメーション
 */
export function drawDataStream(
    p: p5,
    x: number,
    _y: number,
    height: number
): void {
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    const streamLen = 200;
    const speed = 2;
    const offset = (p.frameCount * speed) % height;

    ctx.save();

    // クリッピングで範囲を制限
    ctx.beginPath();
    ctx.rect(x, 0, 2, height);
    ctx.clip();

    const gradient = ctx.createLinearGradient(x, offset - streamLen, x, offset);
    gradient.addColorStop(0, 'rgba(0, 240, 255, 0)');
    gradient.addColorStop(0.3, 'rgba(0, 240, 255, 0.5)');
    gradient.addColorStop(0.6, 'rgba(0, 240, 255, 0.3)');
    gradient.addColorStop(1, 'rgba(0, 240, 255, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(x, offset - streamLen, 2, streamLen);

    // ラップアラウンド（画面下端を超えたら上にも描画）
    if (offset < streamLen) {
        const wrapGradient = ctx.createLinearGradient(x, height + offset - streamLen, x, height + offset);
        wrapGradient.addColorStop(0, 'rgba(0, 240, 255, 0)');
        wrapGradient.addColorStop(0.3, 'rgba(0, 240, 255, 0.5)');
        wrapGradient.addColorStop(0.6, 'rgba(0, 240, 255, 0.3)');
        wrapGradient.addColorStop(1, 'rgba(0, 240, 255, 0)');
        ctx.fillStyle = wrapGradient;
        ctx.fillRect(x, height + offset - streamLen, 2, streamLen);
    }

    ctx.restore();
}

/**
 * drawHudDivider() - セクション間のグラデーション水平線
 * シアンから透明へのフェード
 */
export function drawHudDivider(
    p: p5,
    x: number,
    y: number,
    width: number
): void {
    const ctx = p.drawingContext as CanvasRenderingContext2D;

    ctx.save();
    const gradient = ctx.createLinearGradient(x, y, x + width, y);
    gradient.addColorStop(0, `rgba(${HUD_COLORS.cyan[0]}, ${HUD_COLORS.cyan[1]}, ${HUD_COLORS.cyan[2]}, 0.3)`);
    gradient.addColorStop(1, 'rgba(0, 240, 255, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, width, 1);
    ctx.restore();
}

/**
 * drawPulseDot() - ラベル横の点滅ドット
 * sin波で 0.6〜1.0 のアルファ変動
 */
export function drawPulseDot(
    p: p5,
    x: number,
    y: number
): void {
    const alpha = (Math.sin(p.frameCount * 0.05) + 1) / 2 * 0.4 + 0.6;
    p.noStroke();
    p.fill(HUD_COLORS.cyan[0], HUD_COLORS.cyan[1], HUD_COLORS.cyan[2], alpha * 255);
    p.circle(x, y, 4);
}
